import { useCallback } from 'react'
import { toast } from 'sonner'
import type { Lang } from '@/types/lang'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { parseStartError } from '@/features/ai/lib/parseStartError'
import { courseLanguageFailureText } from '@/features/ai/lib/courseLanguageFailureText'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { refusedByReview } from '../../lib/reviewGuard'
import { setContentLang } from '../../editorSlice'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import {
  useChangeSourceLangMutation,
  useDeclareTranslationMutation,
  useDeleteTranslationMutation,
  useSetTranslationPublishedMutation,
} from '../../coursesApi'
import { translationPublishKey } from '../../lib/courseTranslation'
import { resolveLang } from '../studio/seedContentLang'

/**
 * Языковые действия курса — одни на шапку и настройки: второй копии условий
 * и текстов отказов быть не должно (спека §02: «оба места читают одни данные»).
 *
 * Отказ — тостом со СВОИМ текстом: ручки в `SILENT_ENDPOINTS`, а меню шапки к
 * моменту ответа уже закрыто, строке ошибки жить негде. По той же причине
 * тостом говорим и об успехе: иначе закрытое меню оставляет автора гадать,
 * сработало ли действие.
 *
 * Языки — часть заявки на проверку: пока курс на ней (`isFrozen`), каждое
 * действие отказывает само, а оба места гасят свои контролы по тому же флагу.
 */
export const useLanguageActions = (course: CourseWithoutBlocks) => {
  const dispatch = useAppDispatch()
  const current = useAppSelector((s) => s.editor.contentLang)
  const [changeSourceLang, changing] = useChangeSourceLangMutation()
  const [deleteTranslation, deleting] = useDeleteTranslationMutation()
  const [declareTranslation, declaring] = useDeclareTranslationMutation()
  // Пока меняется видимость, удалить или сменить язык нельзя: действия ждут и эту запись.
  const [, publishing] = useSetTranslationPublishedMutation({
    fixedCacheKey: translationPublishKey(course.id),
  })
  const source = resolveLang(course.sourceLang)
  const isFrozen = useIsCourseFrozen(course.id)

  const makeBase = useCallback(
    async (lang: Lang): Promise<boolean> => {
      if (lang === source) return true
      if (refusedByReview(isFrozen)) return false
      // Спека §07: автор не должен сомневаться, что прежняя версия цела. Тост
      // сразу на клике (кеш курса меняется оптимистично); отказ заменит его ошибкой
      // по тому же id.
      const id = toast.success(messagesNow(messages).madeBase(lang, source))
      try {
        await changeSourceLang({ courseId: course.id, sourceLang: lang }).unwrap()
        return true
      } catch (error) {
        toast.dismiss(id)
        toast.error(
          courseLanguageFailureText(parseStartError(error), messagesNow(messages).makeBaseFailed),
        )
        return false
      }
    },
    [changeSourceLang, course.id, source, isFrozen],
  )

  const remove = useCallback(
    async (lang: Lang): Promise<boolean> => {
      if (refusedByReview(isFrozen)) return false
      const id = toast.success(messagesNow(messages).langDeleted(lang))
      try {
        await deleteTranslation({ courseId: course.id, lang }).unwrap()
        // Слот стёрт — продолжать писать в него значит писать в пустоту.
        if (current === lang) dispatch(setContentLang(source))
        return true
      } catch (error) {
        toast.dismiss(id)
        toast.error(
          courseLanguageFailureText(parseStartError(error), messagesNow(messages).deleteLangFailed),
        )
        return false
      }
    },
    [deleteTranslation, course.id, current, dispatch, source, isFrozen],
  )

  /** Добавить язык: объявить строку на сервере. Слот правки НЕ переключается
   *  (решение заказчика): язык просто включается, а выбранным остаётся тот, что
   *  был, — перейти на новый автор может сам из того же меню. */
  const add = useCallback(
    async (lang: Lang): Promise<boolean> => {
      if (refusedByReview(isFrozen)) return false
      try {
        await declareTranslation({ courseId: course.id, lang }).unwrap()
        return true
      } catch {
        toast.error(messagesNow(messages).addLangFailed)
        return false
      }
    },
    [declareTranslation, course.id, isFrozen],
  )

  return {
    makeBase,
    remove,
    add,
    isBusy: changing.isLoading || deleting.isLoading || declaring.isLoading || publishing.isLoading,
    /** Когда сервер принял последнюю запись; после отказа её нет. */
    writtenAt: Math.max(
      changing.fulfilledTimeStamp ?? 0,
      deleting.fulfilledTimeStamp ?? 0,
      declaring.fulfilledTimeStamp ?? 0,
      publishing.fulfilledTimeStamp ?? 0,
    ),
    isFrozen,
  }
}
